import { loginStaff } from "../features/ops/mock-store.ts";
import type { StaffPermission, StaffPrincipal, StaffRole } from "../features/ops/types.ts";
import { api } from "./api.ts";
import { getAuthToken, setAuthToken } from "./auth-token.ts";
import { env } from "./env.ts";

const TOKEN_KEY = "jod.staff.token";
const PRINCIPAL_KEY = "jod.staff.principal";

function storage(): Storage | null {
  return typeof window === "undefined" ? null : window.sessionStorage;
}

type StaffLoginEnvelope = {
  success: boolean;
  data?: {
    accessToken: string;
    staff: {
      id: string;
      staffCode: string;
      displayName: string;
      venueId: string;
      branchId: string | null;
      roles: StaffRole[];
      permissions: StaffPermission[];
    };
  } | null;
  error?: { code?: string; message?: string } | null;
};

function persist(principal: StaffPrincipal, token: string): void {
  setAuthToken(token);
  storage()?.setItem(TOKEN_KEY, token);
  storage()?.setItem(PRINCIPAL_KEY, JSON.stringify(principal));
}

/**
 * Sign in with a server-verified staff PIN (FR-STAFF-001/002). In backend
 * mode this calls the real `POST /api/v1/auth/staff/login` endpoint — PIN
 * verification, identity, branch scope, and role/permission resolution all
 * happen server-side; the client never asserts them. The local mock store is
 * used only when the app is explicitly running in mock mode
 * (`VITE_API_MOCK=1`), for local UX demos without a backend.
 */
export async function signInStaff(staffCode: string, pin: string): Promise<StaffPrincipal | null> {
  if (env.apiMock) {
    const principal = loginStaff(staffCode, pin);
    if (!principal) return null;
    persist(principal, `staff-demo-${principal.id}`);
    return principal;
  }

  const result = await api.POST("/api/v1/auth/staff/login", {
    body: { venueId: env.staffVenueId, staffCode, pin },
  });
  const envelope = result.data as StaffLoginEnvelope | undefined;
  if (!envelope?.success || !envelope.data) return null;

  const { accessToken, staff } = envelope.data;
  const principal: StaffPrincipal = {
    id: staff.id,
    displayName: staff.displayName,
    staffCode: staff.staffCode,
    branchId: staff.branchId ?? "",
    roles: staff.roles,
    permissions: staff.permissions,
  };
  persist(principal, accessToken);
  return principal;
}

export function signOutStaff(): void {
  setAuthToken(undefined);
  storage()?.removeItem(TOKEN_KEY);
  storage()?.removeItem(PRINCIPAL_KEY);
}

export function getStaffPrincipal(): StaffPrincipal | null {
  const raw = storage()?.getItem(PRINCIPAL_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as StaffPrincipal;
  } catch {
    return null;
  }
}

export function isStaffAuthenticated(): boolean {
  const token = getAuthToken() ?? storage()?.getItem(TOKEN_KEY) ?? undefined;
  if (token) setAuthToken(token);
  return Boolean(token);
}

export function getStaffRedirectTarget(pathname: string): "/login" | null {
  return pathname === "/login" || isStaffAuthenticated() ? null : "/login";
}
